// @flow

import Downshift from 'downshift';
import React from 'react';

import Menu from './Menu';
import Toggle from './Toggle';

import type { ComponentType } from 'react';

// We can't safely assume that `Item` will always have a specific shape, because consumers that customize
// rendering might have items with additional/different properties flowing through.
type Item = *;

type Props = {
  /**
   * Callback that will be used to generate the react `key` to be used for each item on the menu list.
   * Defaults to a `value` property on each item object.
   */
  getKeyForItem?: (item: Item) => string,
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: Item) => string,
  /** `true` if this select field is in a loading state */
  isLoading: boolean,
  /** `true` to hide Toggle border */
  isBorderHidden?: boolean,
  /**
   * The list of items. Each item gets passed as props to `MenuItemComponent` to be rendered.
   */
  items: Array<Item>,
  /** Show dark background toggle */
  isDark?: boolean,
  /** Disables entire input */
  isDisabled?: boolean,
  /**
   * Callback that will be used to determine if an item is disabled.
   * Defaults to the value of `item.isDisabled`.
   */
  isItemDisabled: (item: Item) => boolean,
  /** id of the label that connects to this select field.
   * **NOTE** You will probaby need this field to pass accessibilty checks.
   * It should be the same value as the `id` for a label. See below
   */
  labelId?: string,
  /** a11y label that describes the options available inside the select menu, e.g. "List of items", "List of Countries", etc. */
  menuAriaLabel: string,
  /**
   * The Component to use for rendering each item in the list of `items`. Defaults to an internal implementation.
   */
  MenuItemComponent?: ComponentType<*>,
  /** Callback that gets called when the user selects and item and the selected item has changed. */
  onChange: (item: *) => void,
  /** Callback that gets called whenever the dropdown is opened */
  onOpen?: (Array<Item>) => void,
  /** Placeholder to render inside the select toggle when no item is selected. */
  placeholder: string,
  /**
   * Optional prop for setting the currently selected item.
   * When omitted, `SelectField` manages this internally for you, but passing in this prop effectively transforms this
   * into a controled prop and `SelectField` will no longer manage its lifecycle internally (meaning you'll have to
   * handle changes via `onChange`, etc.).
   */
  selectedItem?: Item,
  /**
   * The Component to use for rendering the selection content inside the toggle.
   * WARNING: If you override this, you're also responsible for handling the cases where:
   * - `selectedItem` is empty
   * - `isLoading` is `true`.
   */
  SelectionComponent?: ComponentType<*>,
};

const defaultProps = {
  getLabelForItem: (item: Item, isConfirming: ?boolean) =>
    isConfirming ? 'Are You Sure?' : item.label,
  isDisabled: false,
  isLoading: false,
  isItemDisabled: (item: Item) => item.isDisabled,
};

/**
 *
 * A replacement for a regular `<select>` field.
 *
 * Features:
 * - Allows customizing how the field looks like when there's something selected
 * - Allows customizing how the dropdown items render
 * - Keyboard support
 * - a11y friendly
 *
 * Future improvements:
 * - Supports grouping items by sections
 *
 * **For most case** You'll want some kind of label like `FormLabel` for this component to point at.
 * This is an accessibility issue. The label should have an id that is the same value that will be passed to the `labelId` prop.
 * The code examples below follow this pattern.
 *
 */
const SelectField = (props: Props) => {
  return (
    <Downshift
      defaultHighlightedIndex={0}
      itemToString={item => (item ? props.getLabelForItem(item) : '')}
      labelId={props.labelId}
      onChange={props.onChange}
      onStateChange={changes =>
        props.onOpen && changes.isOpen ? props.onOpen(props.items) : null
      }
      selectedItem={props.selectedItem}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        getToggleButtonProps,
        highlightedIndex,
        isOpen,
        selectedItem,
        toggleMenu,
      }) => (
        <div
          // downshift defaults to setting a role of `combobox` here, but this role is only valid if it has an `<input>` element as a child.
          // Since this component renders a toggle but not an `<input>` element, let's remove the `combobox` role. The `Menu` itself
          // below renders with a `menu` role, which should suffice for a11y purposes.
          // (As can be seen in: https://w3c.github.io/aria-practices/examples/listbox/listbox-collapsible.html)
          style={{ position: 'relative' }}
        >
          <Toggle
            getInputProps={getInputProps}
            getLabelForItem={props.getLabelForItem}
            getToggleButtonProps={getToggleButtonProps}
            isBorderHidden={props.isBorderHidden}
            isDark={props.isDark}
            isDisabled={props.isDisabled || !props.items.length}
            isLoading={props.isLoading}
            onClick={toggleMenu}
            placeholder={props.placeholder}
            rootProps={{
              ...getRootProps(
                { refKey: 'innerRef' },
                { suppressRefError: true }
              ),
            }}
            selectedItem={selectedItem}
            SelectionComponent={props.SelectionComponent}
          />
          <Menu
            ariaLabel={props.menuAriaLabel}
            getItemProps={getItemProps}
            getKeyForItem={props.getKeyForItem}
            getLabelForItem={props.getLabelForItem}
            getMenuProps={getMenuProps}
            highlightedIndex={highlightedIndex}
            isItemDisabled={props.isItemDisabled}
            isOpen={isOpen}
            items={props.items}
            MenuItemComponent={props.MenuItemComponent}
          />
        </div>
      )}
    </Downshift>
  );
};

SelectField.defaultProps = defaultProps;

export default SelectField;
